'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const searchTabs = ['Buy', 'Rent', 'Off-Plan', 'Commercial', 'Holiday Homes', 'Hotels'];

const locations = [
  'Dubai Marina', 'Palm Jumeirah', 'Downtown Dubai', 'Business Bay',
  'Jumeirah', 'Al Reem Island', 'Saadiyat Island', 'Yas Island',
  'The Pearl – Qatar', 'KAEC', 'Riyadh Hills'
];

const propertyTypes = [
  'All Types', 'Apartment', 'Villa', 'Penthouse', 'Townhouse',
  'Studio', 'Office', 'Retail', 'Warehouse'
];

const bedroomOptions = ['Any', 'Studio', '1', '2', '3', '4', '5+'];

const heroStats = [
  { value: '48,200+', label: 'Active Listings' },
  { value: '2.1M+', label: 'Monthly Visitors' },
  { value: '99.2%', label: 'Verified Listings' },
  { value: 'AED 3.8B+', label: 'Transactions Facilitated' },
];

export default function HeroSection() {
  const [activeTab, setActiveTab] = useState('Buy');
  const [location, setLocation] = useState('');
  const [propertyType, setPropertyType] = useState('All Types');
  const [bedrooms, setBedrooms] = useState('Any');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);

  const filteredLocations = locations?.filter(l =>
    l?.toLowerCase()?.includes(location?.toLowerCase())
  );

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-hero-gradient" />
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C9B37E' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />
      {/* Decorative orbs */}
      <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full bg-primary-light/20 blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 left-1/4 w-64 h-64 rounded-full bg-accent/10 blur-3xl pointer-events-none" />
      <div className="relative z-10 w-full max-w-screen-2xl mx-auto px-6 lg:px-10 pt-32 pb-20">
        {/* Hero Text */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6">
            <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
            <span className="text-white/90 text-sm font-medium">UAE&apos;s #1 AI-Powered Real Estate Platform</span>
          </div>

          <h1 className="text-5xl xl:text-7xl font-extrabold text-white leading-tight tracking-tight mb-6 text-balance">
            Find Your Perfect
            <span className="block text-gradient-champagne">Property World</span>
          </h1>

          <p className="text-white/70 text-lg xl:text-xl max-w-2xl mx-auto leading-relaxed">
            Search 48,000+ verified properties across the UAE — Dubai, Abu Dhabi, Sharjah, and beyond — powered by AI intelligence and government-grade verification.
          </p>
        </div>

        {/* Search Card */}
        <div className="max-w-5xl mx-auto glass-card rounded-3xl p-1.5 shadow-elevated">
          {/* Tabs */}
          <div className="flex gap-1 p-1 bg-muted/60 rounded-2xl mb-1">
            {searchTabs?.map((tab) => (
              <button
                key={`hero-tab-${tab}`}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  activeTab === tab
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-foreground/70 hover:text-primary hover:bg-white/50'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Search Fields */}
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
              {/* Location */}
              <div className="xl:col-span-2 relative">
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">
                  Location
                </label>
                <div className="relative">
                  <Icon name="MapPinIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary" />
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => {
                      setLocation(e?.target?.value);
                      setShowLocationDropdown(true);
                    }}
                    onFocus={() => setShowLocationDropdown(true)}
                    onBlur={() => setTimeout(() => setShowLocationDropdown(false), 200)}
                    placeholder="Area, community, or landmark"
                    className="w-full pl-9 pr-4 py-3 bg-muted border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                  />
                  {showLocationDropdown && filteredLocations?.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-card rounded-xl shadow-elevated border border-border z-50 py-1 max-h-48 overflow-y-auto">
                      {filteredLocations?.map((loc) => (
                        <button
                          key={`loc-${loc}`}
                          onMouseDown={() => { setLocation(loc); setShowLocationDropdown(false); }}
                          className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-primary/8 hover:text-primary transition-colors flex items-center gap-2"
                        >
                          <Icon name="MapPinIcon" size={14} className="text-muted-foreground" />
                          {loc}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Property Type */}
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">
                  Type
                </label>
                <select
                  value={propertyType}
                  onChange={(e) => setPropertyType(e?.target?.value)}
                  className="w-full px-3 py-3 bg-muted border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                >
                  {propertyTypes?.map((t) => (
                    <option key={`type-${t}`} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              {/* Bedrooms */}
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">
                  Bedrooms
                </label>
                <div className="flex gap-1">
                  {bedroomOptions?.slice(0, 5)?.map((b) => (
                    <button
                      key={`bed-${b}`}
                      onClick={() => setBedrooms(b)}
                      className={`flex-1 py-3 rounded-lg text-xs font-semibold transition-all duration-150 ${
                        bedrooms === b
                          ? 'bg-primary text-white' :'bg-muted text-foreground/70 hover:bg-primary/10 hover:text-primary border border-border'
                      }`}
                    >
                      {b}
                    </button>
                  ))}
                </div>
              </div>

              {/* Search Button */}
              <div className="flex flex-col justify-end">
                <Link
                  href="/property-listings-page"
                  className="w-full flex items-center justify-center gap-2 bg-primary text-white py-3 rounded-xl font-bold text-sm hover:bg-primary-dark transition-all duration-200 active:scale-95 shadow-sm"
                >
                  <Icon name="MagnifyingGlassIcon" size={18} />
                  Search
                </Link>
              </div>
            </div>

            {/* Budget & Advanced */}
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e?.target?.value)}
                    placeholder="Min AED"
                    className="w-28 px-3 py-2 bg-muted border border-border rounded-lg text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                  />
                  <span className="text-muted-foreground text-xs">—</span>
                  <input
                    type="text"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e?.target?.value)}
                    placeholder="Max AED"
                    className="w-28 px-3 py-2 bg-muted border border-border rounded-lg text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                  />
                </div>
              </div>
              <Link
                href="/advanced-search-page"
                className="flex items-center gap-1.5 text-primary text-xs font-semibold hover:underline"
              >
                <Icon name="AdjustmentsHorizontalIcon" size={14} />
                Advanced Filters
              </Link>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="flex items-center justify-center gap-8 xl:gap-16 mt-12 flex-wrap">
          {heroStats?.map((stat) => (
            <div key={`hero-stat-${stat?.label}`} className="text-center">
              <div className="text-2xl xl:text-3xl font-extrabold text-accent font-tabular">{stat?.value}</div>
              <div className="text-white/60 text-xs font-medium mt-0.5">{stat?.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}